import { Layout } from "@jambushrushti/components/layout/Layout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, ExternalLink } from "lucide-react";

const newsItems: {
  id: number;
  title: string;
  titleEn: string;
  date: string;
  location: string;
  description: string;
  image: string;
  type: string;
}[] = [];

const News = () => {
  return (
    <Layout>
      {/* Hero */}
      <section
        className="py-5 sm:py-8 lg:py-16"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <div className="container mx-auto px-4">
          <div className="animate-fade-up mx-auto max-w-3xl text-center">
            <p
              className="mb-2 text-xs font-semibold uppercase tracking-wider sm:mb-3 sm:text-sm"
              style={{ color: "#D2811D", fontFamily: "Inter, sans-serif" }}
            >
              Our News
            </p>
            <h1
              className="mb-3 text-3xl font-bold sm:mb-6 sm:text-5xl"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              News <span style={{ fontStyle: "italic" }}>&</span> Announcements
            </h1>
            <p className="text-base sm:text-lg" style={{ color: "#6B6764" }}>
              Stay informed about events, updates, and announcements from
              Jambushrusti and the Siddhi Jambu Parivar community.
            </p>
          </div>
        </div>
      </section>

      {/* Featured News */}
      <section className="bg-background py-6 sm:py-10 lg:py-16">
        <div className="container mx-auto px-4">
          {newsItems.length > 0 && (
            <Card
              variant="feature"
              className="animate-fade-up mb-8 overflow-hidden sm:mb-12"
            >
              <div className="grid gap-0 md:grid-cols-2">
                <div className="aspect-[4/3] overflow-hidden md:aspect-auto">
                  <img
                    src={newsItems[0].image}
                    alt={newsItems[0].titleEn}
                    className="h-full w-full object-cover"
                  />
                </div>
                <CardContent className="flex flex-col justify-center p-5 sm:p-8">
                  <div className="mb-3 flex items-center gap-3 sm:mb-4">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                      {newsItems[0].type}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Featured
                    </span>
                  </div>
                  <h2 className="font-display mb-2 text-xl font-bold text-foreground sm:text-2xl">
                    {newsItems[0].title}
                  </h2>
                  <p className="mb-2 text-sm text-muted-foreground sm:text-base">
                    {newsItems[0].titleEn}
                  </p>
                  <p className="mb-4 text-sm text-muted-foreground sm:text-base">
                    {newsItems[0].description}
                  </p>
                  <div className="mb-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>{newsItems[0].date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>{newsItems[0].location}</span>
                    </div>
                  </div>
                  <Button variant="hero" className="w-fit">
                    Read More
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          )}

          {/* News Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {newsItems.slice(1).map((news, index) => (
              <Card
                key={news.id}
                variant="interactive"
                className="animate-fade-up group overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.titleEn}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <CardContent className="p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="rounded-full bg-secondary px-2 py-1 text-xs text-muted-foreground">
                      {news.type}
                    </span>
                  </div>
                  <h3 className="font-display mb-1 line-clamp-2 font-semibold text-foreground transition-colors group-hover:text-primary">
                    {news.titleEn}
                  </h3>
                  <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                    {news.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{news.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{news.location}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* External Events Link */}
      <section className="bg-gradient-spiritual py-6 sm:py-10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-4 text-xl font-bold text-foreground sm:text-2xl">
            View All Events
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-sm text-muted-foreground sm:text-base">
            Visit the official Siddhi Jambu Parivar website for complete event
            listings
          </p>
          <div className="mx-auto grid max-w-md grid-cols-2 gap-2.5 sm:flex sm:max-w-none sm:flex-wrap sm:justify-center sm:gap-4">
            <Button
              variant="hero"
              asChild
              className="w-full px-2.5 text-xs sm:w-auto sm:px-6 sm:text-sm"
            >
              <a
                href="https://siddhijambuparivar.com/events-list/"
                target="_blank"
                rel="noopener noreferrer"
                className="justify-center"
              >
                View All Events
                <ExternalLink className="ml-1.5 h-3.5 w-3.5 shrink-0 sm:ml-2 sm:h-4 sm:w-4" />
              </a>
            </Button>
            <Button
              variant="outline"
              asChild
              className="w-full px-2.5 text-xs sm:w-auto sm:px-6 sm:text-sm"
            >
              <Link to="/community/events" className="justify-center">
                Community Events
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default News;
